import client, { getWebSocketUrl } from '@api/client';
// ★ 이 셋은 반드시 `@engine/debugFlags` 에서 가져온다.
//   전엔 앞의 둘을 `blockStream` · `objectBaked` 에서 가져왔는데, 그 두 모듈이
//   three 로 이어져 **로그인 화면이 Three.js 2MB 를 정적으로 받았다.** debugFlags 는
//   three 를 안 문다 — 여기로 되돌리지 마라.
import { BLOCK_STREAM_ON } from '@engine/debugFlags';

/**
 * 게임 관련 실시간 및 데이터 API
 */
const gameApi = {
  /**
   * 게임 웹소켓 연결 생성
   */
  createSocket: (userId, nickname, characterId) => {
    // character는 접속 직후 서버가 join 브로드캐스트에 실어야 해서 URL 쿼리로 전달(첫 메시지로는 늦음).
    // nickname은 경로가 아닌 쿼리로 보낸다. `%2F` 는 일부 ASGI 서버가 경로 구분자로
    // 디코딩해 연결을 거부할 수 있으므로 사용자 입력 문자열을 path segment에 넣지 않는다.
    // 블록 청크 스트리밍 스위치도 URL 로 간다(character 와 같은 이유 — 첫 메시지로는 늦다).
    // 서버는 이걸 보고 **통짜 sync_blocks 를 건너뛴다**. 스위치가 양쪽에서 하나여야
    // 「둘 다 받는」 상태가 안 생긴다. 기본은 꺼짐 — 오늘까지의 경로가 그대로 돈다.
    const parts = [];
    if (nickname) parts.push(`nickname=${encodeURIComponent(nickname)}`);
    if (characterId) parts.push(`character=${encodeURIComponent(characterId)}`);
    if (BLOCK_STREAM_ON) parts.push('stream=chunks');
    const query = parts.length ? `?${parts.join('&')}` : '';
    const wsUrl = getWebSocketUrl(`/api/game/ws/${encodeURIComponent(String(userId))}${query}`);
    // ── 인증 토큰은 **헤더로** 간다 ─────────────────────────────
    // 브라우저 WebSocket 은 임의 헤더를 못 붙인다. 쿼리로 보내는 게 흔한 우회지만,
    // 이 박스는 nginx·uvicorn 이 **둘 다 쿼리스트링을 로그에 남긴다** — 7일짜리 토큰을
    // 거기 실으면 로그 파일이 곧 전 유저의 자격증명 창고가 된다.
    // `Sec-WebSocket-Protocol` 은 브라우저가 표준으로 실어 보낼 수 있는 유일한 헤더이고
    // 로그에 안 찍힌다. 서버는 앞의 이름만 골라 돌려준다(비밀을 안 되돌려준다).
    // ⚠ 이름은 서버 `WS_AUTH_PROTO` 와 같아야 한다 — 값이 두 언어에 산다.
    const token = (() => {
      try { return localStorage.getItem('access_token') || ''; } catch { return ''; }
    })();
    return token ? new WebSocket(wsUrl, ['game.auth.token', token]) : new WebSocket(wsUrl);
  },

  /**
   * 위치 데이터 전송 포맷팅
   */
  /**
   * 위치 데이터 전송 포맷팅 (Backend Protocol Matching)
   */
  sendPosition: (socket, positionData) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      const payload = {
        type: 'position', // event -> type
        position: {
          x: positionData.x,
          y: positionData.y || 0, // y가 없으면 0 (지상)
          z: positionData.z
        },
        rotation: positionData.rotation,
        animation: positionData.animation || 'Idle',
        mapId: positionData.mapId // [NEW] 맵 정보 추가
      };
      socket.send(JSON.stringify(payload));
      return true;
    }
    return false;
  },

  sendPlayerEmote: (socket, emote) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    socket.send(JSON.stringify({ type: 'player_emote', emote }));
    return true;
  },

  /**
   * 채팅 데이터 전송 포맷팅
   */
  sendChat: (socket, message) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      // "/p 메시지"=파티 채팅 — 파티원에게만 전송
      let scope;
      let text = message;
      if (typeof text === 'string' && (text.startsWith('/p ') || text.startsWith('/파티 '))) {
        scope = 'party';
        text = text.replace(/^\/(p|파티)\s+/, '');
      }
      const payload = {
        type: 'chat', // event -> type
        message: text,
        ...(scope ? { scope } : {}),
        timestamp: Date.now()
      };
      socket.send(JSON.stringify(payload));
    }
  },

  /**
   * 현재 서버 접속 중인 전체 플레이어 목록 (회원/게스트 구분, 실시간 WS 기준)
   */
  getOnlinePlayers: () => client.get('/api/game/players/online'),

  /**
   * 내 모험 기록(player 행) — 입장 화면의 「모습 고르기」에서 진짜 진행도를 보여주기 위한 것.
   * 캐릭터가 없으면 {character:null} (신규·게스트). 조회가 캐릭터를 만들지는 않는다.
   */
  getMySummary: (userId) => client.get('/api/game/me/summary', { params: { user_id: userId } }),

  /** 길잡이를 만났다고 표시(067). **캐릭터 단위** — 계정 단위인 시네마틱 시청과 다른 축이다. */
  markMetGuide: (userId) => client.post('/api/game/me/met-guide', null, { params: { user_id: userId } }),

  /**
   * 유저 설정 조회
   */
  getSettings: (userId) => client.get(`/api/game/settings/${userId}`),

  /**
   * 유저 설정 저장
   */
  saveSettings: (userId, settings) => client.put(`/api/game/settings/${userId}`, { settings }),
  // 스킬 퀵슬롯 (103) — 캐릭터별. settings 와 «갈라 둔» 이유는 라우터 주석 참고:
  //   같은 JSONB 안에 두면 설정 저장 한 번이 퀵슬롯을 덮어쓴다(부분 갱신이 서로를 밟는다).
  // job = 그 배치의 직업 — 서버가 지금 직업 칸이 아닌 그 직업 칸을 읽고 쓴다(전환 중 엇갈림 방지).
  getHotbar: (userId, job) => client.get(`/api/game/hotbar/${userId}`, { params: job ? { job } : undefined }),
  // 생활 숙련(126) — 창을 열 때 한 번 읽는다. 보상 문장까지 **서버가 계산해서** 준다:
  // 클라가 곡선을 또 갖고 있으면 밸런스를 한 번 손보는 순간 화면만 옛 값을 말한다.
  getLifeSkill: (userId) => client.get(`/api/game/life-skill/${userId}`),
  saveHotbar: (userId, slots, job) => client.put(`/api/game/hotbar/${userId}`, { slots, job }),

  /**
   * 몬스터 피격 데이터 전송
   */
  sendHit: (socket, hitData) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      const payload = {
        type: 'hit_monster',
        monsterId: hitData.monsterId,
        skillName: hitData.skillName
      };
      // 평타 3단 콤보 — 마무리 타 배수는 **서버가** 매긴다. 여기서는
      // 「몇 번째 타였나」만 주장하고, 상한(COMBO_MULT)은 서버가 안다.
      //
      // ⚠ 이 함수는 payload 를 **다시 짓는다** — hitData 를 그대로 보내지 않는다.
      //   그래서 새 필드는 여기 한 줄을 같이 안 적으면 조용히 사라진다. 실제로
      //   `aimed`(AIM_BONUS ×1.5)가 그렇게 빠져 있다: 시전부와 ProjectileLayer 는
      //   싣는데 여기서 버려져 서버 배수가 **한 번도 안 붙은 적이 없다**.
      //   콤보를 같은 방식으로 잃지 않으려고 이 주석을 여기 둔다.
      if (hitData.combo) payload.combo = hitData.combo;
      if (hitData.comboSplit) payload.comboSplit = hitData.comboSplit;
      socket.send(JSON.stringify(payload));
    }
  }
};

export default gameApi;
